import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, 
  SpaceBetween, 
  Header, 
  Tabs,
  DateRangePicker,
  DateRangePickerProps,
  Select,
  TextContent
} from '@amzn/awsui-components-react';
import { LogsView } from '../CWInsightQuery/LogsView';
import { VisualizationView } from '../CWInsightQuery/VisualizationView';
import { useJunoFleetMetricsPCQuery } from '../../../hooks/useJunoFleetMetricsPCQuery';
import moment from 'moment';
import { i18nStrings, timezoneOptions } from '../AuditHistory/utils';
import { isValidRange, convertTZ } from '../../../../utils/timeUtils';

const relativeOptions: ReadonlyArray<DateRangePickerProps.RelativeOption> = [
  { amount: 1, unit: 'hour', type: 'relative', key: '1h' },
  { amount: 3, unit: 'hour', type: 'relative', key: '3h' },
  { amount: 6, unit: 'hour', type: 'relative', key: '6h' },
  { amount: 12, unit: 'hour', type: 'relative', key: '12h' },
  { amount: 1, unit: 'day', type: 'relative', key: '1d' },
  { amount: 3, unit: 'day', type: 'relative', key: '3d' },
  { amount: 7, unit: 'day', type: 'relative', key: '7d' }
];

interface FleetMetricsProps {
  placementConstraintId: string;
  dataPlaneType: string;
  awsAccountId: string;
  namespace: string;
  flavor: string;
}

const FleetMetrics: React.FC<FleetMetricsProps> = ({ 
  placementConstraintId, 
  dataPlaneType,
  awsAccountId,
  namespace,
  flavor
}) => {
  const [activeTabId, setActiveTabId] = useState('logs');
  const [selectedTimeZoneOption, setSelectedTimeZoneOption] = useState({
    inputDisplay: 'Browser Timezone',
    label: 'Browser Timezone',
    value: 'Browser Timezone',
    code: '',
  });
  const [currentDate, setCurrentDate] = useState<DateRangePickerProps.Value>({
    type: 'relative',
    amount: 6,
    unit: 'hour',
    key: '6h'
  });

  const { loading, error, data, fetchData } = useJunoFleetMetricsPCQuery();

  useEffect(() => {

    fetchData(currentDate, {
      placementConstraintId,
      dataPlaneType,
      awsAccountId,
      namespace,
      flavor
    });
    
  }, [placementConstraintId, dataPlaneType, awsAccountId, namespace, flavor]);
  
  const logsData = useMemo(() => {

    if (!data?.getJunoFleetMetricsPC) return [];
    
    return data.getJunoFleetMetricsPC.map(item => {
      if (!item.metrics || item.metrics.length === 0) return { 
        timestamp: convertTZ(item.timestamp, selectedTimeZoneOption) 
      };
      
      const { epoch, timestamp: metricsTimestamp, ...metricsFields } = item.metrics[0];
      
      return {
        timestamp: convertTZ(item.timestamp, selectedTimeZoneOption),
        ...metricsFields
      };
    });
  }, [data, selectedTimeZoneOption]);

  const visualizationData = useMemo(() => {
    if (!data?.getJunoFleetMetricsPC) return [];
    
    return data.getJunoFleetMetricsPC.map(item => {
      if (!item.metrics || item.metrics.length === 0) return { timestamp: convertTZ(item.timestamp, selectedTimeZoneOption) };
      
      const numericFields = Object.entries(item.metrics[0])
        .filter(([key, value]) => typeof value === 'number' && key !== 'epoch')
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as Record<string, number>);
      
      return {
        timestamp: convertTZ(item.timestamp, selectedTimeZoneOption),
        ...numericFields
      };
    });
  }, [data, selectedTimeZoneOption]);

  const handleTimezoneChange = ({ detail }) => {
    setSelectedTimeZoneOption(detail.selectedOption);
  };

  const isRelativeValue = (value: DateRangePickerProps.Value): value is DateRangePickerProps.RelativeValue => {
    return value.type === 'relative';
  };
  
  const handleDateChange: DateRangePickerProps['onChange'] = ({ detail }) => {
    let startMoment, endMoment;
  
    if (!detail.value) {
      startMoment = moment().subtract(1, 'day');
      endMoment = moment();
      setCurrentDate({
        type: 'relative',
        amount: 6,
        unit: 'hour',
        key: '6h'
      });
    } else if (detail.value.type === 'absolute') {
      startMoment = moment(detail.value.startDate);
      endMoment = moment(detail.value.endDate);
      setCurrentDate(detail.value);
    } else if (isRelativeValue(detail.value)) {
      endMoment = moment();
      startMoment = moment().subtract(detail.value.amount, detail.value.unit);
      setCurrentDate(detail.value);
    }
  
    if (startMoment && endMoment) {
      const timeRange = !detail.value ? {
        type: 'relative' as const,
        amount: 6,
        unit: 'hour' as DateRangePickerProps.TimeUnit,
        key: '6h'
      } : detail.value;
  
      fetchData(timeRange, {
        placementConstraintId,
        dataPlaneType,
        awsAccountId,
        namespace,
        flavor
      });
    }
  };

  const tabs = useMemo(() => [
    {
      label: 'Logs',
      id: 'logs',
      content: <LogsView data={logsData} />
    },
    {
      label: 'Visualization',
      id: 'visualization',
      content: <VisualizationView 
        data={visualizationData} 
        selectedTimeZone={selectedTimeZoneOption}
      />
    }
  ], [logsData, visualizationData, selectedTimeZoneOption]);

  if (error) {
    return (
      <Box color="text-status-error" textAlign="center" padding="l">
        Error loading data: {error.message}
      </Box>
    );
  }

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <Header 
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="s">
              <DateRangePicker
                onChange={handleDateChange}
                value={currentDate}
                expandToViewport={true}
                relativeOptions={relativeOptions}
                isValidRange={isValidRange}
                placeholder="Filter by a date and time range"
                i18nStrings={i18nStrings}
              />
              <TextContent>
                <strong>Timezone:</strong>
              </TextContent>
              <Select
                selectedOption={selectedTimeZoneOption}
                onChange={handleTimezoneChange}
                options={timezoneOptions}
                selectedAriaLabel="Selected"
              />
            </SpaceBetween>
          }
        >
          {flavor === 'flexo_iwdm' ? 'Flexo Metrics' : 'Worker Metrics'}
        </Header>
        {loading ? (
          <Box textAlign="center" padding="l">
            Loading...
          </Box>
        ) : (
          <Tabs
            tabs={tabs}
            activeTabId={activeTabId}
            onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          />
        )}
      </SpaceBetween>
    </Box>
  );
};

export default FleetMetrics;